import type { TOC } from '@ember/component/template-only';

export interface MicromodalSignature {
  Element: HTMLDivElement;
  Blocks: {
    default: [typeof OverlayElement, typeof DialogElement];
  };
}

const OverlayElement: TOC<{
  Element: HTMLDivElement;
  Blocks: { default: [] };
}> = <template>
  <div ...attributes tabindex='-1'>
    {{yield}}
  </div>
</template>;

const DialogElement: TOC<{ Element: HTMLDivElement }> = <template>
  {{! aria-labelledby will be set by the MicromodalContentComponent }}
  {{! TODO: pre-existing, see the eslint-plugin-ember migration ticket }}
  {{! eslint-disable-next-line ember/template-no-invalid-aria-attributes }}
  <div role='dialog' aria-modal='true' ...attributes aria-labelledby=''>
    {{! will be filled by in-element helper }}
  </div>
</template>;

/**
 * The Micromodal component is just a simple component that factorizes the
 * markup required by the "micromodal" lib
 *
 * It is basically only 3 nested HTMLDivElements, each with the proper aria
 * attributes set.
 *
 * If you decide to use this component in its non-block form, ie <Micromodal />,
 * you'll get the 3 nested HTMLDivElements properly inserted in the DOM, and you
 * will be able to style them using the splattributes with
 * <Micromodal class="foo bar" />
 *
 * If you decide to use its block form, it'll insert the outter div in the DOM,
 * and yield the overlay and dialog components for you to use as you wish. This
 * approach offers more flexibility to style the different HTMLDivElements, but
 * requires that you properly setup the HTMLDivElements in the DOM.
 *
 * <Micromodal class="foo" as |Overlay Dialog|>
 *   <Overlay class="bar">
 *     <Dialog class="bazz">
 *       {{! will be filled by in-element helper }}
 *     <Dialog />
 *   </Overlay>
 * </Micromodal>
 */
const MicromodalComponent: TOC<MicromodalSignature> = <template>
  <div ...attributes aria-hidden='true'>
    {{#if (has-block)}}
      {{yield OverlayElement DialogElement}}
    {{else}}
      <OverlayElement>
        <DialogElement>
          {{! will be filled by in-element helper }}
        </DialogElement>
      </OverlayElement>
    {{/if}}
  </div>
</template>;

export default MicromodalComponent;
